import { useMemo, useCallback } from 'react'

import { useCreateReview } from './useCreateReview'
import { useProductReviews } from './useProductReviews'
import { useReviewRatings } from './useReviewRatings'

import type { ReviewsState, ReviewFormValues } from '../types'

export interface ReviewsStateValue extends ReviewsState {
  submitReview: (review: ReviewFormValues) => Promise<void>
  isSubmitting: boolean
}

/**
 * Composition hook for reviews state
 * Combines reviews data, ratings, and mutation logic
 *
 * IMPORTANT: useMemo is REQUIRED here because this hook's return value is used
 * directly as Provider context value in ReviewsProvider. Without useMemo,
 * every render creates a new object reference, causing all context consumers to re-render.
 */
export function useReviewsState(productSku: string): ReviewsStateValue {
  const reviewsQuery = useProductReviews(productSku)
  const ratingsQuery = useReviewRatings()
  const createReviewMutation = useCreateReview()

  const submitReview = useCallback(
    async (review: ReviewFormValues) => {
      await createReviewMutation.mutateAsync(review)
    },
    [createReviewMutation]
  )

  return useMemo(
    () => ({
      reviews: reviewsQuery.reviews,
      ratings: ratingsQuery.ratings,
      neededRating: ratingsQuery.neededRating,
      submitReview,
      isSubmitting: createReviewMutation.isPending,
      loading: reviewsQuery.loading || ratingsQuery.loading,
      error: reviewsQuery.error || ratingsQuery.error || createReviewMutation.error,
    }),
    [
      createReviewMutation.error,
      createReviewMutation.isPending,
      ratingsQuery.error,
      ratingsQuery.loading,
      ratingsQuery.neededRating,
      ratingsQuery.ratings,
      reviewsQuery.error,
      reviewsQuery.loading,
      reviewsQuery.reviews,
      submitReview,
    ]
  )
}
